import React, { memo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { styled } from '@nykaa/ui-components';
import PageTypes from 'constants/pageTypes';
import { Device } from 'shared/types';
import { State } from 'store/types';
import { useIsConfigHeader, useIsThemeHeader } from 'hooks/header';
import { AB_TOP_NAV_CONFIG } from 'constants/experiments';
import { getEligibleForVariant } from 'shared/helpers/experimentV2';
import { useIsAddressBarEnabled } from 'hooks/header';
import LeftNav from './LeftNav';
import RightNav from './RightNav';
import Search from './Search';
import { getPageType, getWrapperBgColor } from './helper';
import { isMen } from '@nykaa/seo/utils';

const ADDRESS_BAR_BG =
  'linear-gradient(0deg, rgba(255, 255, 255, 0.58) 0%, rgba(194, 199, 214, 0.61) 84.62%)';
const ADDRESS_BAR_BACKDROP = 'blur(40px)';

const Wrapper = styled.header<{
  isThemeHeader: boolean;
  headerBg?: string;
  isAddressBarEnabled?: boolean;
}>`
  background: ${({ theme, isThemeHeader, headerBg, isAddressBarEnabled }) =>
    isAddressBarEnabled
      ? ADDRESS_BAR_BG
      : getWrapperBgColor(theme, isThemeHeader, headerBg)};
  ${({ isAddressBarEnabled }) =>
    isAddressBarEnabled ? `backdrop-filter: ${ADDRESS_BAR_BACKDROP};` : ''}
  position: relative;
  z-index: 3;
  position: sticky;
  top: 0px;
`;

const Container = styled.div<{ showSearchBar: boolean; isMen?: boolean }>`
  ${(props) =>
    props.showSearchBar &&
    `
    padding: ${props.isMen ? '0 0 12px' : '0 0 8px'};
  `};
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.1);
`;

const Top = styled.div`
  padding: 4px 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

interface Props {
  device: Device;
  topNavConfig: {
    status: boolean;
    enabledPages: string[];
  };
  isStickyHeader: boolean;
}

const Header = ({ device, topNavConfig, isStickyHeader }: Props) => {
  const { pathname } = useLocation();
  const isThemeHeader = useIsThemeHeader();
  const { headerBg } = useIsConfigHeader();
  const headerWithSearchBarEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_TOP_NAV_CONFIG, 'A')
  );
  const isAddressBarEnabled = useIsAddressBarEnabled();
  if (device === Device.WEB) {
    const { status, enabledPages } = topNavConfig;
    const pageType = getPageType(pathname);

    const isOldSearchEnabled =
      headerWithSearchBarEnabled && pageType === PageTypes.PRODUCT;
    // If old Search is enabled in that case old searchbar design will be shown,
    // doesn't matter topNavConfig is enabled or not.
    const newNavEnabled =
      !isOldSearchEnabled &&
      status &&
      isStickyHeader &&
      enabledPages?.includes(pageType);
    return (
      <Wrapper
        id="app-header"
        className={newNavEnabled ? 'sticky-header' : ''}
        isThemeHeader={isThemeHeader}
        headerBg={headerBg}
        isAddressBarEnabled={isAddressBarEnabled}
      >
        <Container
          showSearchBar={!newNavEnabled}
          isMen={isMen()}
        >
          <Top>
            <LeftNav
              newNavEnabled={newNavEnabled}
              pageType={pageType}
            />
            <RightNav newNavEnabled={newNavEnabled} />
          </Top>
          {!newNavEnabled && <Search />}
        </Container>
      </Wrapper>
    );
  }

  return null;
};

Header.displayName = 'Header';

export default memo(Header);
